import type { FNode, FNodeChild } from './types'
import type { SerializedState } from '../server/types'
import { runWithComponent, type ComponentInstance } from '../core/hook'
import { pushContext, popContext } from '../core/context'
import { unsafeEffect } from '../core/lifecycle'
import { renderNode, reconcile } from './render'

// Hydration state
let isHydrating = false
let hydrationCursor: Node | null = null
let hydrationState: SerializedState | null = null

export interface HydrateOptions {
  /**
   * Serialized state from server
   * Typically embedded in HTML as JSON script tag
   */
  state?: SerializedState

  /**
   * Called when hydration completes successfully
   */
  onHydrated?: () => void

  /**
   * Called when hydration fails (falls back to full render)
   */
  onMismatch?: (error: Error) => void
}

// Extended ComponentInstance for DOM tracking (mirrors render.ts shape).
// Phase 5C: pre-initialize every field at construction; `null` is the
// unallocated sentinel for slots that get populated later.
interface DOMComponentInstance extends ComponentInstance {
  nodes: Node[]
  parent: HTMLElement
  fnode: FNode
  props: Record<string, unknown>
  key: unknown
  renderFn: (() => void) | null
  children: Set<DOMComponentInstance>
  parentInstance: DOMComponentInstance | null
}

// Registry for hydrated components
const hydratedInstanceRegistry = new WeakMap<HTMLElement, Map<unknown, DOMComponentInstance>>()
let currentHydratingInstance: DOMComponentInstance | null = null

export function getIsHydrating(): boolean {
  return isHydrating
}

export function getHydrationState(): SerializedState | null {
  return hydrationState
}

/**
 * Hydrate server-rendered HTML with client-side interactivity
 */
export function hydrate(
  app: FNodeChild | (() => FNodeChild),
  container: HTMLElement,
  options: HydrateOptions = {}
): void {
  const { state, onHydrated, onMismatch } = options

  // Store state for rehydration
  hydrationState = state || null

  // Initialize hydration mode
  isHydrating = true
  hydrationCursor = container.firstChild

  try {
    // Normalize input
    let fnode: FNodeChild
    if (typeof app === 'function' && !isFNode(app)) {
      fnode = { type: app, props: {}, children: [], key: undefined }
    } else {
      fnode = app
    }

    // Hydrate the tree
    hydrateNode(fnode, container)

    onHydrated?.()
  } catch (error) {
    // Hydration mismatch - fall back to full render
    onMismatch?.(error as Error)

    // Clear and re-render
    container.innerHTML = ''
    // Import dynamically to avoid circular deps
    import('./render').then(({ render }) => {
      render(app, container)
    })
  } finally {
    isHydrating = false
    hydrationCursor = null
    hydrationState = null
  }
}

function isFNode(value: unknown): value is FNode {
  return value !== null && typeof value === 'object' && 'type' in value && 'props' in value
}

function hydrateNode(fnode: FNodeChild, parent: HTMLElement): Node | Node[] | null {
  // Null/undefined/boolean -> skip empty text nodes
  if (fnode === null || fnode === undefined || typeof fnode === 'boolean') {
    // Server might have rendered an empty text node
    skipEmptyTextNodes()
    return null
  }

  // String/number -> expect text node
  if (typeof fnode === 'string' || typeof fnode === 'number') {
    return hydrateTextNode(String(fnode))
  }

  // Array -> hydrate each child
  if (Array.isArray(fnode)) {
    const nodes: Node[] = []
    for (const child of fnode) {
      const result = hydrateNode(child, parent)
      if (result) {
        if (Array.isArray(result)) {
          nodes.push(...result)
        } else {
          nodes.push(result)
        }
      }
    }
    return nodes
  }

  // Function (standalone) -> wrap in FNode and hydrate
  if (typeof fnode === 'function') {
    const wrappedFnode: FNode = { type: fnode, props: {}, children: [], key: undefined }
    return hydrateComponent(wrappedFnode, parent)
  }

  // Object (FNode)
  if (typeof fnode === 'object' && isFNode(fnode)) {
    if (typeof fnode.type === 'string') {
      return hydrateElement(fnode)
    }

    if (typeof fnode.type === 'function') {
      return hydrateComponent(fnode, parent)
    }
  }

  return null
}

function skipEmptyTextNodes(): void {
  while (
    hydrationCursor &&
    hydrationCursor.nodeType === Node.TEXT_NODE &&
    (!hydrationCursor.textContent || hydrationCursor.textContent.trim() === '')
  ) {
    hydrationCursor = hydrationCursor.nextSibling
  }
}

function hydrateTextNode(text: string): Node | null {
  skipEmptyTextNodes()

  const current = hydrationCursor

  if (!current) {
    // No node to hydrate - this is okay for conditional rendering
    return null
  }

  if (current.nodeType !== Node.TEXT_NODE) {
    // Try to find a text node nearby (whitespace handling)
    if (text.trim() === '') {
      return null
    }
    throw new Error(`Hydration mismatch: expected text node "${text}", got ${current.nodeName}`)
  }

  // Update cursor
  hydrationCursor = current.nextSibling

  return current
}

function hydrateElement(fnode: FNode): Node {
  skipEmptyTextNodes()

  const current = hydrationCursor as Element
  const tag = fnode.type as string

  // Validate element type
  if (!current || current.nodeType !== Node.ELEMENT_NODE) {
    throw new Error(`Hydration mismatch: expected element <${tag}>, got ${current?.nodeName || 'nothing'}`)
  }

  if (current.tagName.toLowerCase() !== tag.toLowerCase()) {
    throw new Error(`Hydration mismatch: expected <${tag}>, got <${current.tagName.toLowerCase()}>`)
  }

  // Attach event handlers and refs (don't modify DOM structure)
  if (fnode.props) {
    for (const [key, value] of Object.entries(fnode.props)) {
      if (key === 'ref') {
        if (typeof value === 'function') {
          value(current)
        } else if (value && typeof value === 'object' && 'current' in value) {
          value.current = current
        }
      } else if (key.startsWith('on') && typeof value === 'function') {
        const eventName = key.slice(2).toLowerCase()
        current.addEventListener(eventName, value as EventListener)

        // Store for reconciliation
        type ElWithHandlers = Element & { __eventHandlers?: Record<string, EventListener> }
        const elWithHandlers = current as ElWithHandlers
        if (!elWithHandlers.__eventHandlers) {
          elWithHandlers.__eventHandlers = {}
        }
        elWithHandlers.__eventHandlers[eventName] = value as EventListener
      }
    }
  }

  // Move cursor past this element
  hydrationCursor = current.nextSibling

  // Hydrate children
  if (fnode.children && fnode.children.length > 0) {
    const savedCursor = hydrationCursor

    hydrationCursor = current.firstChild

    for (const child of fnode.children) {
      hydrateNode(child, current as HTMLElement)
    }

    hydrationCursor = savedCursor
  }

  return current
}

function hydrateComponent(fnode: FNode, parent: HTMLElement): Node | Node[] | null {
  const Component = fnode.type as (props: Record<string, unknown>) => FNodeChild

  // Merge props
  const props: Record<string, unknown> = { ...fnode.props }
  if (fnode.children && fnode.children.length > 0) {
    props.children = fnode.children.length === 1
      ? fnode.children[0]
      : fnode.children
  }

  // Handle context providers
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ._contextId is a dynamic property set by Context constructor on Provider functions
  const contextId = (Component as any)._contextId as symbol | undefined
  const isProvider = contextId !== undefined
  let prevContextValue: unknown

  if (isProvider) {
    prevContextValue = pushContext(contextId, props.value)
  }

  // Generate key (same logic as render.ts)
  if (!hydratedInstanceRegistry.has(parent)) {
    hydratedInstanceRegistry.set(parent, new Map())
  }
  const parentRegistry = hydratedInstanceRegistry.get(parent)!

  const hasExplicitKey = fnode.key !== undefined
  let key: unknown
  if (hasExplicitKey) {
    key = fnode.key
  } else {
    let instanceCount = 0
    const componentName = Component.name || 'anonymous'
    parentRegistry.forEach((_, k) => {
      if (typeof k === 'string' && k.startsWith(`__auto_${componentName}_`)) {
        instanceCount++
      }
    })
    key = `__auto_${componentName}_${instanceCount}`
  }

  // Create component instance. Phase 5C: every field pre-initialized.
  const instance: DOMComponentInstance = {
    hooks: [],
    hookIndex: 0,
    componentName: Component.name || 'Component',
    hookSignatures: null,
    prevHookSignatures: null,
    effects: null,
    globalKeys: null,
    nodes: [],
    parent,
    fnode,
    props,
    key,
    renderFn: null,
    children: new Set(),
    parentInstance: currentHydratingInstance ?? null,
  }

  if (currentHydratingInstance) {
    currentHydratingInstance.children.add(instance)
  }

  parentRegistry.set(key, instance)

  const previousHydratingInstance = currentHydratingInstance
  currentHydratingInstance = instance

  try {
    // First render during hydration - just match DOM
    const result = runWithComponent(instance, () => Component(props))
    const nodes = hydrateNode(result, parent)
    instance.nodes = nodes ? (Array.isArray(nodes) ? nodes : [nodes]) : []

    // Set up reactive re-rendering for future updates
    let isFirstRender = true
    const renderFn = () => {
      // Re-render with reconciliation (same logic as render.ts)
      const currentProps = instance.props

      // Always run the component to establish reactive tracking
      const result = runWithComponent(instance, () => Component(currentProps))

      if (isFirstRender) {
        isFirstRender = false
        return // Skip DOM manipulation on first render, already done during hydration
      }

      if (instance.nodes.length === 0) {
        const newNodes = renderNode(result, parent)
        instance.nodes = newNodes ? (Array.isArray(newNodes) ? newNodes : [newNodes]) : []
        return
      }

      const firstNode = instance.nodes[0]
      const nodeParent = firstNode.parentNode as HTMLElement

      if (!nodeParent) {
        return
      }

      const marker = document.createComment('flexium-marker')
      const lastNode = instance.nodes[instance.nodes.length - 1]
      if (lastNode.nextSibling) {
        nodeParent.insertBefore(marker, lastNode.nextSibling)
      } else {
        nodeParent.appendChild(marker)
      }

      instance.children.clear()

      const tempContainer = document.createElement('div')
      const newNodes = renderNode(result, tempContainer, nodeParent)
      const newNodesArray = newNodes ? (Array.isArray(newNodes) ? newNodes : [newNodes]) : []

      const reconciledNodes = reconcile(instance.nodes, newNodesArray, nodeParent, marker)

      nodeParent.removeChild(marker)

      instance.nodes = reconciledNodes
    }

    instance.renderFn = renderFn
    // Framework-internal root effect: drives this component's reactive re-renders after hydration.
    // Use .detached so it is NOT registered on the parent component — ownership is
    // tracked via DOMComponentInstance itself and stopped in removeComponentInstance.
    unsafeEffect.detached(renderFn)

    return instance.nodes
  } finally {
    currentHydratingInstance = previousHydratingInstance

    if (isProvider) {
      popContext(contextId, prevContextValue)
    }
  }
}
